/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { Button, LoadingOverlay, TextField } from "@insite/mobius/index";
import Page from "@insite/mobius/Page";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@werner/App/Components/Notify/Notify";
import checkPassword from "@werner/App/Handlers/ProtectedContent/CheckPassword";
import loadSystemList from "@werner/App/Handlers/SystemList/SystemListHandler";
import { CustomAppState } from "@werner/App/Store/Reducers";
import { passwordCookieName } from "@werner/XDL/constants";
import { xValidateOnSubmit } from "@werner/XDL/Utilities";
import * as React from "react";
import { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const systemListName = "Werner-PasswordProtectedCMSPages";

const mapStateToProps = (state: CustomAppState) => {
    const currentPageUrl = getCurrentPage(state)?.fields.urlSegment;

    return {
        protectedPagesList: state.xSystemList[systemListName],
        currentPageUrl,
    };
};

const mapDispatchToProps = {
    checkPassword,
    loadSystemList,
};

interface OwnProps {
    children: React.ReactNode;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    HasShellContext &
    HasHistory &
    ResolveThunks<typeof mapDispatchToProps>;

export interface ProtectedContentStyles {
    passwordProtectedMessage: TypographyProps;
    passwordFieldWrapper: InjectableCss;
    submitButtonWrapper: InjectableCss;
}

export const protectedContentStyles: ProtectedContentStyles = {
    passwordProtectedMessage: {
        size: 15,
        weight: 500,
        css: css`
            line-height: 17px;
            letter-spacing: 0;
            margin-bottom: 12px;
        `,
    },

    passwordFieldWrapper: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: 30px;
            justify-content: flex-end;
            & > button {
                width: auto;
                min-width: unset;
            }
        `,
    },

    submitButtonWrapper: {
        css: css`
            margin-top: 20px;
            display: flex;
            justify-content: flex-end;
        `,
    },
};
const StyledForm = getStyledWrapper("form");

const styles = protectedContentStyles;

const formId = "protected-content-form";

const ProtectedContentWidget: FC<Props> = ({
    shellContext,
    checkPassword,
    protectedPagesList,
    loadSystemList,
    children,
    currentPageUrl,
}) => {
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const [passwordError, setPasswordError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [password, setPassword] = useState("");

    if (!protectedPagesList && isFirstLoad) {
        if (!isLoading) {
            setIsLoading(true);
        }
        loadSystemList({
            systemListName,
            onSuccess: () => {
                setIsLoading(false);
            },
            onError: () => {
                setIsLoading(false);
            },
        });
    }

    const checkIsValidPassowrd = (password: string, showErrorMessage = true) => {
        if (!isLoading) {
            setIsLoading(true);
        }
        checkPassword({
            password,
            onSuccess: (result: any) => {
                setIsLoading(false);

                if (!result || (typeof result === "object" && !Object.keys(result).length)) {
                    setPasswordError(showErrorMessage);
                    return;
                }
                setCookie(passwordCookieName, result);
                setIsAuthenticated(true);
            },
            onError: (error: any) => {
                logger.error(error);
                setIsLoading(false);
            },
        });
    };

    React.useEffect(() => {
        const hasPassword = !!getCookie(passwordCookieName);
        setIsFirstLoad(false);
        if (hasPassword) {
            checkIsValidPassowrd(getCookie(passwordCookieName)!, false);
        } else {
            setIsLoading(false);
        }
    }, []);

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const errors = xValidateOnSubmit(formId);
        if (errors.length > 0) {
            return;
        }
        checkIsValidPassowrd(password);
    };

    const handlePasswordChange = (event: any) => {
        setPasswordError(false);
        setPassword(event.target.value);
    };

    const isRestrictedPage = (systemListPage: string, currentPageUrl: string) => {
        const currentPageWithSlash = currentPageUrl?.startsWith("/") ? currentPageUrl : `/${currentPageUrl || ""}`;
        const systemListPageWithSlash = systemListPage?.startsWith("/") ? systemListPage : `/${systemListPage || ""}`;
        return currentPageWithSlash.toLowerCase() === systemListPageWithSlash.toLowerCase();
    };

    const isInludedInProtectedPages =
        currentPageUrl && protectedPagesList?.values?.some(value => isRestrictedPage(value.name, currentPageUrl));
    const showProtectedWidget =
        !isInludedInProtectedPages || (isInludedInProtectedPages && isAuthenticated) || shellContext.isInShell;

    return (
        <>
            {!showProtectedWidget && (
                <Page>
                    <LoadingOverlay
                        loading={isLoading}
                        css={css`
                            width: 100%;
                        `}
                    >
                        <>
                            {createWidgetElement("Basic/PageTitle", {
                                fields: {},
                            })}
                            <StyledWrapper id="passwordProtectedWidget">
                                <StyledForm onSubmit={handleSubmit} noValidate>
                                    <Typography variant="h2" {...styles.passwordProtectedMessage}>
                                        {siteMessage("Werner_Layout_RestrictedPagePasswordLabel")}
                                    </Typography>
                                    <StyledWrapper {...styles.passwordFieldWrapper}>
                                        <TextField
                                            type="password"
                                            value={password}
                                            data-test-selector="passwordProtectedField"
                                            onChange={handlePasswordChange}
                                            label={translate("Enter Password")}
                                            required={true}
                                            {...{ formId, disableMask: true, messageLabel: translate("Password") }}
                                        />

                                        {passwordError && (
                                            <Notify type="danger">
                                                {siteMessage("Werner_Layout_RestrictedPageIncorrectPassword")}
                                            </Notify>
                                        )}
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.submitButtonWrapper}>
                                        <Button type="submit" sizeVariant="medium">
                                            {translate("Submit")}
                                        </Button>
                                    </StyledWrapper>
                                </StyledForm>
                            </StyledWrapper>
                        </>
                    </LoadingOverlay>
                </Page>
            )}
            {showProtectedWidget && children}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withHistory(ProtectedContentWidget)));
